import { useState, useEffect, useCallback } from "react";
import api, { fmtDate, errDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DataTable } from "../components/DataTable";
import { PageHeader } from "../components/CrudPage";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = { giris: "Giriş", cikis: "Çıkış", satis: "Satış", iade: "İade" };
const TYPE_STYLES = {
  giris: "bg-emerald-50 text-emerald-600", cikis: "bg-red-50 text-red-600",
  satis: "bg-blue-50 text-blue-600", iade: "bg-amber-50 text-amber-600",
};

export default function StockMovements() {
  const { hasPerm } = useAuth();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_id: "", type: "giris", quantity: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/stock-movements", { params: { page, search, limit: 15, type: typeFilter } });
      setData(d);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setLoading(false); }
  }, [page, search, typeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/products", { params: { limit: 200 } }).then((r) => setProducts(r.data.items)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.product_id || !form.quantity) { toast.error("Ürün ve miktar zorunludur"); return; }
    setSaving(true);
    try {
      await api.post("/stock-movements", { ...form, quantity: Number(form.quantity) });
      toast.success("Stok hareketi kaydedildi");
      setOpen(false);
      setForm({ product_id: "", type: "giris", quantity: "", note: "" });
      load();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="stock-movements-page">
      <PageHeader title="Stok Hareketleri" subtitle="Tüm stok giriş, çıkış, satış ve iade hareketleri">
        {hasPerm("stock_movements", "create") && (
          <Button data-testid="stock-movement-add-button" onClick={() => setOpen(true)}
            className="rounded-none bg-[#D4AF37] font-semibold text-[#0B1F3A] hover:bg-[#c6a230]">
            <Plus size={18} className="mr-1" /> Yeni Hareket
          </Button>
        )}
      </PageHeader>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(searchInput); }} className="flex items-center gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input data-testid="stock-search-input" className="w-56 rounded-none pl-9" placeholder="Ürün ara..."
              value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          </div>
          <Button type="submit" variant="outline" className="rounded-none">Ara</Button>
        </form>
        <Select value={typeFilter || "all"} onValueChange={(v) => { setPage(1); setTypeFilter(v === "all" ? "" : v); }}>
          <SelectTrigger data-testid="stock-type-filter" className="w-40 rounded-none"><SelectValue placeholder="Tür" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tür: Tümü</SelectItem>
            {Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <DataTable testId="stock-movements-table" columns={[
        { key: "created_at", label: "Tarih", render: (r) => fmtDate(r.created_at) },
        { key: "product_name", label: "Ürün", render: (r) => <span className="font-medium">{r.product_name}</span> },
        { key: "type", label: "Tür", render: (r) => <span className={`px-2 py-1 text-xs font-semibold ${TYPE_STYLES[r.type] || ""}`}>{TYPE_LABELS[r.type] || r.type}</span> },
        { key: "quantity", label: "Miktar" },
        { key: "note", label: "Not" },
        { key: "user_name", label: "Kullanıcı" },
      ]} data={data.items} loading={loading} page={page} pages={data.pages} total={data.total} onPage={setPage} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Yeni Stok Hareketi</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Ürün *</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="movement-product-select" className="rounded-none"><SelectValue placeholder="Ürün seçin" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (Stok: {p.stock})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Hareket Türü *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="movement-type-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="giris">Giriş</SelectItem>
                    <SelectItem value="cikis">Çıkış</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Miktar *</Label>
                <Input data-testid="movement-quantity-input" type="number" className="rounded-none"
                  value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Not</Label>
              <Input data-testid="movement-note-input" className="rounded-none" value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button data-testid="movement-save-button" disabled={saving} onClick={save}
                className="rounded-none bg-[#0B1F3A] text-white hover:bg-[#132c4f]">Kaydet</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
